import {
  getAddedAndUpdatedItems,
  GraphNode,
  GraphItem,
  GraphItemsModel,
  ComponentGraphNode,
} from '@ardoq/graph';
import { ViewLoadMetrics } from 'tracking/durationTracking/types';
import { GenericInstanceViewSettings } from 'views/environmentals/viewSettings/types';
import {
  type GraphComponent,
  type LayoutDescriptor,
  ViewportDescriptor,
  ViewportLimitingMode,
} from '@ardoq/yfiles';
import { DisabledZoomControls } from '@ardoq/view-settings';
import { isString } from 'lodash';

const ZOOM_DISABLED_EPSILON = 1e-12;

export const isSelectionStaleAfterNodeUpdate = (
  graphComponent: GraphComponent,
  newNodes: { id: string }[]
): boolean => {
  const currentSelectionIds = graphComponent.selection.nodes
    .map(({ tag = {} }) => tag.id)
    .filter(isString);
  const newNodeIds = new Set(newNodes.map(node => node.id));
  return !currentSelectionIds.every(id => newNodeIds.has(id));
};

type GetZoomControlsDisabledStateArgs = {
  graphComponent: GraphComponent;
  zoomIncrement: number;
  fitWindowDisabled: boolean;
};

export const getZoomControlsDisabledState = ({
  graphComponent,
  zoomIncrement,
  fitWindowDisabled,
}: GetZoomControlsDisabledStateArgs): number => {
  const { zoom, viewport, viewportLimiter } = graphComponent;
  const { center: viewportCenter } = viewport;

  const { zoom: limitedZoomOut } = viewportLimiter.limitViewport(
    graphComponent,
    new ViewportDescriptor(viewportCenter, zoom / zoomIncrement),
    ViewportLimitingMode.PROGRAMMATIC_NAVIGATION
  );
  const { zoom: limitedZoomIn } = viewportLimiter.limitViewport(
    graphComponent,
    new ViewportDescriptor(viewportCenter, zoom * zoomIncrement),
    ViewportLimitingMode.PROGRAMMATIC_NAVIGATION
  );

  const isZoomOutDisabled =
    Math.abs(limitedZoomOut - zoom) < ZOOM_DISABLED_EPSILON;
  const isZoomInDisabled =
    Math.abs(limitedZoomIn - zoom) < ZOOM_DISABLED_EPSILON;

  return (
    (Number(isZoomOutDisabled) && DisabledZoomControls.ZOOM_OUT) |
    (Number(isZoomInDisabled) && DisabledZoomControls.ZOOM_IN) |
    (Number(fitWindowDisabled) && DisabledZoomControls.FIT_WINDOW)
  );
};

export const isFreshLayout = (layoutDescriptor: LayoutDescriptor): boolean =>
  layoutDescriptor.name === 'HierarchicalLayout' &&
  !layoutDescriptor.properties?.fromSketchMode;

type GetComponentWorkspaceId = (componentId: string) => string | null;
type GetWorkspaceCountState = {
  workspaceIds: Set<string>;
  getComponentWorkspaceId: GetComponentWorkspaceId;
};
const collectWorkspaceIds = (
  { workspaceIds, getComponentWorkspaceId }: GetWorkspaceCountState,
  node: GraphNode | ComponentGraphNode
): GetWorkspaceCountState => {
  const workspaceId = getComponentWorkspaceId(node.modelId);
  if (workspaceId) {
    workspaceIds.add(workspaceId);
  }
  return { workspaceIds, getComponentWorkspaceId };
};

type ViewModelForPerformanceTracking<
  TNode extends GraphNode | ComponentGraphNode,
  TEdge extends GraphItem,
> = {
  nodes: GraphItemsModel<TNode>;
  edges: GraphItemsModel<TEdge>;
};

export const getPerformanceTrackingData = <
  TNode extends GraphNode | ComponentGraphNode,
  TEdge extends GraphItem,
>(
  { nodes, edges }: ViewModelForPerformanceTracking<TNode, TEdge>,
  getComponentWorkspaceId: GetComponentWorkspaceId
): ViewLoadMetrics => {
  const nodeItems = getAddedAndUpdatedItems(nodes);
  const edgeItems = getAddedAndUpdatedItems(edges);
  const { workspaceIds } = nodeItems.reduce(collectWorkspaceIds, {
    getComponentWorkspaceId,
    workspaceIds: new Set<string>(),
  });

  return {
    componentCount: nodeItems.length,
    referenceCount: edgeItems.length,
    workspaceCount: workspaceIds.size,
  };
};

export const isSeparateReferencesChange = <
  T extends GenericInstanceViewSettings<Record<string | symbol, any>>,
>(
  settingsWithChanges: T
) =>
  settingsWithChanges.changedSettings.length === 1 &&
  settingsWithChanges.changedSettings[0][0] === 'separateReferences';

const setAsUpdated = <T extends GraphItem>(
  graphItems: GraphItemsModel<T>
): GraphItemsModel<T> => ({
  byId: graphItems.byId,
  add: [],
  update: [...graphItems.update, ...graphItems.add],
  remove: [],
});

export const cloneGraphItemsAsUpdate = <
  TNode extends GraphItem,
  TGroup extends GraphItem,
  TEdge extends GraphItem,
>(args: {
  nodes: GraphItemsModel<TNode>;
  groups: GraphItemsModel<TGroup>;
  edges: GraphItemsModel<TEdge>;
}) => ({
  expandedStateChangingGroupId: null,
  nodes: setAsUpdated(args.nodes),
  groups: setAsUpdated(args.groups),
  edges: setAsUpdated(args.edges),
});
